import * as XLSX from "xlsx";
import moment from "moment";

/**
 * Import test atletici (portieri) da file CSV/XLSX esportato da OptoJump.
 *
 * Il file OptoJump ha un layout "a matrice" con più sessioni impilate:
 *  - una riga titolo per sessione (es. "OPTOJUMP AGOSTO 2026")
 *  - una riga intestazione ("Portieri", "Anno", "Cm", "Kg", parametri...)
 *  - una riga sotto-intestazione (Dx/Sx, t.c./Cm/Rsi/T. Volo...)
 *  - N righe giocatore (cognome, anno, valori)
 *
 * Le colonne dei valori sono mappate per OFFSET rispetto alla colonna
 * "Portieri" (cognome): il template OptoJump è a posizioni fisse e questo
 * è più robusto del parsing delle intestazioni unite/multilivello.
 */

// Offset (rispetto alla colonna cognome) -> valueType del parametro app "Spinte".
// Le colonne saltate (altezza/peso e i "Rsi" non previsti dall'app) non sono mappate.
export const SPINTE_OFFSET_MAP = {
	4: "SJ", // SQUAT JAMP
	5: "SJ mono dx", // SQUAT JAMP dx
	6: "SJ mono sx", // SQUAT JAMP sx
	7: "CMJ", // CM JUMP VINCOLO
	8: "CMJL", // CM JUMP LIBERO
	9: "CMJ dx", // CM JUMP MONO Vinc Dx
	10: "SJ sx", // CM JUMP MONO Vinc Sx
	11: "CMJL dx", // CM JUMP MONO Libero Dx
	12: "SJL sx", // CM JUMP MONO Libero Sx
	13: "Stiffness TC", // STIFFNESS Vinc t.c.
	14: "Stiffness h", // STIFFNESS Vinc Cm
	16: "Stiffness TV", // STIFFNESS Vinc T. Volo
	17: "Stiffness bl TC", // STIFFNESS Libero Tc
	18: "Stiffness bl h", // STIFFNESS Libero Cm
	20: "Stiffness bl TV", // STIFFNESS Libero T. Volo
	21: 'CMJ 15" TC', // CmJ Libero 15" Tc
	22: 'CMJ 15" h', // CmJ Libero 15" Cm
	24: 'CMJ 15" TV', // CmJ Libero 15" T. Volo
};

// Mesi in italiano -> numero (per ricavare la data di default dalla sessione).
const IT_MONTHS = {
	gennaio: 1,
	febbraio: 2,
	marzo: 3,
	aprile: 4,
	maggio: 5,
	giugno: 6,
	luglio: 7,
	agosto: 8,
	settembre: 9,
	ottobre: 10,
	novembre: 11,
	dicembre: 12,
};

const normalizeText = (value) =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "") // rimuove accenti
		.toUpperCase()
		.replace(/\s+/g, " ")
		.trim();

const round3 = (n) => Math.round(n * 1000) / 1000;

// Converte in numero gestendo i decimali europei.
// Es: "34,5" -> 34.5 ; "1.234,5" -> 1234.5 ; "52.8" -> 52.8 ; 52.80000004 -> 52.8
const toNumber = (value) => {
	if (value === null || value === undefined || value === "") return null;
	if (typeof value === "number")
		return Number.isNaN(value) ? null : round3(value);

	let s = String(value).trim();
	if (!s) return null;
	if (s.includes(",") && s.includes(".")) {
		// entrambi presenti: "." = separatore migliaia, "," = decimali
		s = s.replace(/\./g, "").replace(",", ".");
	} else if (s.includes(",")) {
		s = s.replace(",", ".");
	}
	const n = parseFloat(s);
	return Number.isNaN(n) ? null : round3(n);
};

// Distanza di Levenshtein (per il matching fuzzy dei cognomi).
export const levenshtein = (a, b) => {
	const s = normalizeText(a);
	const t = normalizeText(b);
	if (s === t) return 0;
	if (!s.length) return t.length;
	if (!t.length) return s.length;
	const prev = Array.from({length: t.length + 1}, (_, i) => i);
	for (let i = 1; i <= s.length; i++) {
		let diag = prev[0];
		prev[0] = i;
		for (let j = 1; j <= t.length; j++) {
			const tmp = prev[j];
			const cost = s[i - 1] === t[j - 1] ? 0 : 1;
			prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + cost);
			diag = tmp;
		}
	}
	return prev[t.length];
};

// Determina il profilo (tipo test) di un blocco dalla sua intestazione.
const detectTestType = (headerRow) => {
	const joined = normalizeText((headerRow || []).join(" "));
	if (/SQUAT|CM JUMP|STIFFNESS|CMJ/.test(joined)) return "Spinte";
	if (/METRI|SPRINT|CORS/.test(joined)) return "Corse";
	return null;
};

const isTitleCell = (value) => {
	const t = normalizeText(value);
	// "OPTOJUMP AGOSTO 2026", "SPRINT ...", "CORSE ..." + anno a 4 cifre
	return /(OPTOJUMP|SPRINT|CORSE)\b/.test(t) && /\b\d{4}\b/.test(t);
};

const parseSessionTitle = (value) => {
	const t = normalizeText(value);
	const yearMatch = t.match(/\b(\d{4})\b/);
	const year = yearMatch ? parseInt(yearMatch[1], 10) : null;
	let month = null;
	Object.keys(IT_MONTHS).forEach((m) => {
		if (t.includes(m.toUpperCase())) month = IT_MONTHS[m];
	});
	const defaultDate =
		year && month
			? moment(`${year}-${String(month).padStart(2, "0")}-01`).toDate()
			: null;
	return {rawTitle: String(value).trim(), month, year, defaultDate};
};

// Trova, in una riga, l'indice della colonna "Portieri"/cognome.
const findSurnameColumn = (row) => {
	if (!row) return -1;
	return row.findIndex((c) => {
		const t = normalizeText(c);
		return t === "PORTIERI" || t === "PORTIERE" || t === "COGNOME";
	});
};

/**
 * Legge un workbook (già parsato con XLSX) e ritorna le sessioni con dati.
 * @returns {{sessions: Array, warnings: string[]}}
 */
export const parseAthleticTestsWorkbook = (workbook) => {
	const sessions = [];
	const warnings = [];

	workbook.SheetNames.forEach((sheetName) => {
		const ws = workbook.Sheets[sheetName];
		const rows = XLSX.utils.sheet_to_json(ws, {
			header: 1,
			defval: null,
			blankrows: true,
			// raw:false -> restituisce il testo formattato: preserva i decimali
			// con la virgola ("34,5") evitando che vengano letti come "345".
			raw: false,
		});

		let i = 0;
		while (i < rows.length) {
			const row = rows[i] || [];
			const titleCell = row.find((c) => isTitleCell(c));

			if (!titleCell) {
				i += 1;
				continue;
			}

			const {rawTitle, month, year, defaultDate} =
				parseSessionTitle(titleCell);

			// Cerca la riga intestazione (contiene "Portieri") nelle righe successive.
			let headerIdx = -1;
			for (let j = i + 1; j <= i + 3 && j < rows.length; j++) {
				if (findSurnameColumn(rows[j]) !== -1) {
					headerIdx = j;
					break;
				}
			}

			if (headerIdx === -1) {
				warnings.push(
					`Sessione "${rawTitle}": intestazione "Portieri" non trovata, saltata.`,
				);
				i += 1;
				continue;
			}

			const headerRow = rows[headerIdx];
			const base = findSurnameColumn(headerRow);
			const testType = detectTestType(headerRow);

			if (testType !== "Spinte") {
				// Corse / altri profili: predisposto ma non ancora verificato su file reale.
				warnings.push(
					`Sessione "${rawTitle}": tipo test non riconosciuto come "Spinte" (attualmente supportato solo OptoJump/Spinte).`,
				);
			}

			// Righe dati: da headerIdx+2 (salta la sotto-intestazione) fino a
			// riga vuota o nuovo titolo di sessione.
			const dataRows = [];
			let k = headerIdx + 2;
			while (k < rows.length) {
				const r = rows[k] || [];
				if (r.find((c) => isTitleCell(c))) break; // nuova sessione
				const surname = r[base];
				if (surname === null || String(surname).trim() === "") {
					// riga vuota -> possibile fine blocco: guarda se le successive sono vuote
					const hasMore = (rows[k + 1] || []).some(
						(c) => c !== null && String(c).trim() !== "",
					);
					if (!hasMore) break;
					k += 1;
					continue;
				}

				const values = {};
				Object.keys(SPINTE_OFFSET_MAP).forEach((offset) => {
					const col = base + parseInt(offset, 10);
					const val = toNumber(r[col]);
					if (val !== null) values[SPINTE_OFFSET_MAP[offset]] = val;
				});

				dataRows.push({
					surname: String(surname).trim(),
					year: toNumber(r[base + 1]) ? parseInt(r[base + 1], 10) : null,
					values,
					valuesCount: Object.keys(values).length,
				});
				k += 1;
			}

			const playersWithData = dataRows.filter((d) => d.valuesCount > 0);
			if (playersWithData.length) {
				sessions.push({
					title: rawTitle,
					month,
					year,
					defaultDate,
					testType: testType || "Spinte",
					rows: playersWithData,
				});
			}

			i = k;
		}
	});

	return {sessions, warnings};
};

/**
 * Match di una riga file (cognome + anno) sulla lista giocatori (portieri).
 * @returns {{player: object|null, confidence: 'exact'|'probable'|'none', candidates: object[]}}
 */
export const matchPlayerRow = (fileRow, players) => {
	const target = normalizeText(fileRow.surname);
	const withMeta = (players || []).map((p) => ({
		player: p,
		norm: normalizeText(p.lastName),
		birthYear: p.dateOfBirth ? moment(p.dateOfBirth).year() : null,
		distance: levenshtein(fileRow.surname, p.lastName),
	}));

	// 1) Match esatto cognome + anno
	const exact = withMeta.find(
		(m) => m.norm === target && m.birthYear === fileRow.year,
	);
	if (exact) return {player: exact.player, confidence: "exact", candidates: []};

	// 2) Cognome esatto ma anno diverso -> probabile
	const sameName = withMeta.find((m) => m.norm === target);
	if (sameName)
		return {player: sameName.player, confidence: "probable", candidates: []};

	// 3) Fuzzy (distanza <= 2) con stesso anno -> probabile
	const fuzzySameYear = withMeta
		.filter((m) => m.distance <= 2 && m.birthYear === fileRow.year)
		.sort((a, b) => a.distance - b.distance);
	if (fuzzySameYear.length)
		return {
			player: fuzzySameYear[0].player,
			confidence: "probable",
			candidates: [],
		};

	// 4) Fuzzy (distanza <= 2) qualsiasi anno -> probabile
	const fuzzy = withMeta
		.filter((m) => m.distance <= 2)
		.sort((a, b) => a.distance - b.distance);
	if (fuzzy.length)
		return {player: fuzzy[0].player, confidence: "probable", candidates: []};

	// 5) Nessun match -> lascia scegliere manualmente (candidati più vicini)
	const candidates = withMeta
		.sort((a, b) => a.distance - b.distance)
		.slice(0, 5)
		.map((m) => m.player);
	return {player: null, confidence: "none", candidates};
};

// Legge un File (browser) e ritorna il workbook XLSX.
export const readWorkbookFromFile = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const workbook = XLSX.read(e.target.result, {type: "array"});
				resolve(workbook);
			} catch (err) {
				reject(err);
			}
		};
		reader.onerror = reject;
		reader.readAsArrayBuffer(file);
	});
